// docs/34 — the client's sound API. Everything that makes a noise goes through here:
//
//   playSfx('ui.click')                              a local sound (a random variant, the bank's own level)
//   playSfx('ui.coins', { gain: 0.6, pan: -0.3 })    louder or softer (a linear multiplier), placed left
//   playSfx('voice.orc_taunt', { variant: 3 })       an exact variant (server cues do this, core/audio/cues.ts)
//   startLoop('rain', 'wx.rain', { gain: 0.8 })      a looping bed; setLoopGain / stopLoop retune and end it
//   preloadSfx(['hit.slash', 'swing.blade'])         fetch ahead of need
//   playFile('Ultimate Whoosh/Basic Whoosh (1).wav')  any file of the licensed library, by its path
//   startLoopFile('bed', 'Medieval Fantasy RPG Soundscapes/Cursed Lands.wav')   a library file as a bed
//   playFile('synergy:Animals Sound FX/Dogs/Dog Bark.wav')   a second library's file: its id prefixes the path
//   playMusic('music.battle', { fadeMs: 2600 })      a score track, streamed, crossfading from the last (docs/44)
//   setMusicDuck(0.45) / stopMusic() / onMusicEnded(cb)   lower it under words, end it, hear it finish
//
// Keys are `SfxKey` (generated from content/audio/sfx.banks.json), so a typo fails typecheck. Every
// bank ships in the build, and so does the whole library (libraryPaths lists it), fetched on use.
// Sounds every player must hear alike (combat, the table's own sounds) are chosen by the server and
// arrive as `sfx_score` rows; never pick those locally.
import { AudioEngine, type FileOptions, type LoopOptions, type MusicOptions, type PlayOptions } from './engine';
import type { SfxKey } from './keys.gen';
import { useSettings } from '../stores/settings';
import { cacheEnabled, cachedBytes, cachedObjectUrl, downloadState, startDownload } from './download';

export type { SfxKey } from './keys.gen';
export type { PlayOptions, LoopOptions, FileOptions, MusicOptions } from './engine';
export type { SfxLibraryEntry } from 'virtual:fk-sfx';

/** The one engine. */
export const audio = new AudioEngine();

/** Plays a sound now (or after `delayMs`), at the bank's level × `gain`. False when dropped. */
export function playSfx(key: SfxKey, opts: PlayOptions = {}): boolean {
  return audio.play(key, opts);
}

/** Fetches (and with `decode`, decodes) every variant of these banks ahead of need. */
export function preloadSfx(keys: readonly SfxKey[], decode = false): Promise<void> {
  return audio.preload(keys, decode);
}

export function startLoop(id: string, key: SfxKey, opts: LoopOptions = {}): void { audio.startLoop(id, key, opts); }
export function setLoopGain(id: string, gain: number, rampMs?: number): void { audio.setLoopGain(id, gain, rampMs); }
export function stopLoop(id: string, fadeMs?: number): void { audio.stopLoop(id, fadeMs); }

/** Plays a library file by its path (bus `sfx` at a typical effects level unless `bus` / `gainDb` say otherwise). */
export function playFile(path: string, opts: PlayOptions & FileOptions = {}): boolean { return audio.playFile(path, opts); }
/** A library file as loop `id` (bus `ambience` by default); `stopLoop` ends it like any loop. */
export function startLoopFile(id: string, path: string, opts: LoopOptions & FileOptions = {}): void { audio.startLoopFile(id, path, opts); }
/** Fetches (and with `decode`, decodes) library files ahead of need. */
export function preloadFiles(paths: readonly string[], decode = false): Promise<void> { return audio.preloadFiles(paths, decode); }
/** Every path in the shipped library. */
export function libraryPaths(): Promise<string[]> { return audio.libraryPaths(); }
/** A library file's entry (length, channels, level) once the library manifest has loaded. */
export function libraryEntry(path: string) { return audio.libraryEntry(path); }

/** Plays a track of a music bank (streamed), crossfading from the one playing; `variant` picks the track. */
export function playMusic(key: SfxKey, opts: MusicOptions = {}): boolean { return audio.playMusic(key, opts); }
/** Fades the score out. */
export function stopMusic(fadeMs?: number): void { audio.stopMusic(fadeMs); }
/** Lowers the score under a cinematic's words: 0 = full level, 1 = silent. */
export function setMusicDuck(amount: number, rampMs?: number): void { audio.setMusicDuck(amount, rampMs); }
/** The track playing, if any. */
export function musicNow(): { key: string; variant: number } | undefined { return audio.musicNow(); }
/** `cb` runs when a track plays to its end (not when it is stopped or crossfaded); returns the unsubscribe. */
export function onMusicEnded(cb: (t: { key: string; variant: number }) => void): () => void { return audio.onMusicEnded(cb); }

/** Decodes ahead of time the exact variant a beat or cue will play (a story page's beats, docs/43). */
export function prepareSfx(key: SfxKey, variant: number): void { audio.prepare(key, variant); }

/** Variants in a bank of this build (0 when it was not bundled). */
export function sfxVariantCount(key: SfxKey): number { return audio.bank(key)?.variants.length ?? 0; }

let started = false;

/**
 * Boot: follow the audio settings, decode the `boot` banks (the UI), and expose `window.__fkSfx`
 * for tests. Called once from main.tsx; safe to call again.
 */
export function startAudio(): void {
  if (started) return;
  started = true;
  const apply = (s: ReturnType<typeof useSettings.getState>) =>
    audio.setLevels({ master: s.audio.master, sfx: s.audio.sfx, ambience: s.audio.ambience, music: s.audio.music, muted: s.audio.muted }, s.audio.muteInBackground);
  apply(useSettings.getState());
  useSettings.subscribe(apply);
  audio.context();
  // the web build keeps every sound in Cache Storage, downloaded in the background (docs/44 MM-cache);
  // wired before the first fetch, so the boot sounds read the cache too
  if (cacheEnabled()) {
    audio.loadBytes = cachedBytes;
    audio.musicSrc = cachedObjectUrl;
  }
  void audio.preloadGroup('boot');
  if (cacheEnabled()) startDownload(() => audio.allUrls());
  const w = globalThis as unknown as Record<string, unknown>;
  w['__fkSfx'] = {
    history: () => audio.history.map((h) => ({ ...h })),
    info: () => ({ ...audio.info(), download: { ...downloadState } }),
    clear: () => { audio.history.length = 0; },
    resume: () => audio.resume(),
    version: audio.manifest.version,
  };
}

/**
 * The `encounter` banks: fetched while the party gathers and when a fight opens, so the first swing
 * does not wait on the network; footsteps and swings (the first cues of almost every pulse, ~17 MB
 * as PCM) are decoded too, so the first step lands on its beat.
 */
export function preloadEncounterSfx(): void {
  // queued first, so they lead the fetch pool
  const quick = Object.keys(audio.manifest.banks).filter((k) => k.startsWith('step.') || k.startsWith('swing.'));
  void audio.preload(quick, true);
  void audio.preloadGroup('encounter');
}
